import { useMemo } from 'react';

import { useAuth } from '../providers/auth';
import { IPrivateRoutes, PrivateRoutes } from '../routes/private.routes';

/**
 * Retorna todas as rotas filhas de um path base.
 *
 * Exemplo: getSubmodulesForPath('/modulesHome/accountingConciliation') retorna
 * todas as rotas cujo path comeca com '/modulesHome/accountingConciliation/'
 * (excluindo a propria rota base e as que tem componentEdit/componentShow,
 * que sao subrotas internas).
 *
 * NAO filtra por permissao — use `usePermittedSubmodules` em UIs de navegacao
 * pra evitar exibir links que o usuario nao pode acessar.
 */
export const getSubmodulesForPath = (basePath: string): IPrivateRoutes[] => {
  const prefix = basePath.endsWith('/') ? basePath : `${basePath}/`;

  return PrivateRoutes.filter(route => {
    // Rota com parametro pode entrar como submodulo pelo `submoduleLink` (sem parametro).
    const path = route.submoduleLink ?? route.path;
    if (!path.startsWith(prefix)) return false;
    if (path === basePath) return false;
    // Submodulo DIRETO: nao tem outro '/' depois do prefix
    // (filtra subrotas tipo /modulesHome/X/sub/detalhe)
    const remainder = path.slice(prefix.length);
    if (remainder.includes('/')) return false;
    // Apenas paths "principais" (sem :id, /create, /edit etc)
    if (path.includes(':')) return false;
    // Submódulos aposentados somem de TODA lista de navegação (home + sidebar).
    if (route.hiddenSubmodule) return false;
    return true;
  }).map(route => (route.submoduleLink ? { ...route, path: route.submoduleLink } : route));
};

/**
 * Retorna submodulos filtrados pelas permissoes do usuario logado.
 *
 * Use em todas as UIs de navegacao (sidebar, hub, breadcrumbs) pra evitar que
 * usuarios sem permissao vejam links pra rotas as quais nao tem acesso. Admin
 * sempre ve tudo.
 */
export const usePermittedSubmodules = (basePath: string): IPrivateRoutes[] => {
  const { hasPermission, isAdmin } = useAuth();
  return useMemo(() => {
    const all = getSubmodulesForPath(basePath);
    if (isAdmin()) return all;
    return all.filter(route => hasPermission(route.path));
  }, [basePath, hasPermission, isAdmin]);
};
